import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && (honeypot as string).trim() !== '') {
      setSubmitStatus('success');
      setStatusMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setSubmitStatus('loading');

    try {
      const body = new URLSearchParams();
      body.append('email', formData.get('email') as string);
      body.append('name', formData.get('name') as string);

      const response = await fetch('https://readdy.ai/api/form/d95s7p6a5sk3luk4pis0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setStatusMessage('Thanks for subscribing! Check your inbox for the latest spreadsheet drops.');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if ((serverMsg || '').toLowerCase().includes('spam')) {
          setSubmitStatus('error');
          setStatusMessage('Something went wrong. Please try again.');
        } else {
          setSubmitStatus('error');
          setStatusMessage(serverMsg || 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setSubmitStatus('error');
      setStatusMessage('Network error. Please check your connection.');
    }
  };

  return (
    <footer className="bg-foreground-950 mx-4 md:mx-6 mb-4 md:mb-6 rounded-3xl overflow-hidden">
      <div className="px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.8fr_1fr_1fr_0.9fr_1.1fr] gap-10 md:gap-8">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-semibold mb-4">Join the Sheet</h3>
            <p className="text-foreground-300 text-sm leading-relaxed mb-6 max-w-sm">
              Subscribe for the latest CSSBuy spreadsheet drops, hidden fashion finds, and exclusive deals delivered straight to your inbox.
            </p>
            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3 max-w-sm"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="visually-hidden"
              />
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="bg-transparent border-b border-foreground-700 text-background-50 text-sm py-2 px-0 placeholder:text-foreground-500 focus:outline-none focus:border-background-50 transition-colors"
              />
              <input
                type="email"
                name="email"
                required
                placeholder="Your Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-b border-foreground-700 text-background-50 text-sm py-2 px-0 placeholder:text-foreground-500 focus:outline-none focus:border-background-50 transition-colors"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-background-50 text-foreground-900 text-sm font-semibold rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer mt-2 w-fit disabled:opacity-50"
              >
                {submitStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                <i className="ri-arrow-right-line"></i>
              </button>
              {submitStatus === 'success' && (
                <p className="text-accent-400 text-xs">{statusMessage}</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-primary-400 text-xs">{statusMessage}</p>
              )}
            </form>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">SEO Articles</h4>
            <nav className="flex flex-col gap-3">
              <a href="/cssbuy-spreadsheet-guide-2026" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Spreadsheet Guide 2026</a>
              <a href="/cssbuy-vs-kakobuy" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">CSSBuy vs KakoBuy</a>
              <a href="/cheap-spreadsheet-sneakers-2026" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Budget Sneakers Guide</a>
              <a href="/best-spreadsheet-finds-2026" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Best Finds of 2026</a>
            </nav>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">Agent Reviews</h4>
            <nav className="flex flex-col gap-3">
              <a href="/cssbuy-deep-review" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">CSSBuy Deep Review</a>
              <a href="/kakobuy-deep-review" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">KakoBuy Deep Review</a>
              <a href="/superbuy-deep-review" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Superbuy Deep Review</a>
            </nav>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">CSSBuy Sheet</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Main Store</a>
              <a href="#categories" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">All Categories</a>
            </nav>
          </div>

          <div>
            <h4 className="text-background-50 text-sm font-semibold mb-4">Contact</h4>
            <p className="text-foreground-400 text-sm mb-1">hello@cssspreadsheet.fun</p>
            <p className="text-foreground-400 text-sm mb-6">support@cssspreadsheet.fun</p>
            <h4 className="text-background-50 text-sm font-semibold mb-2">Based In</h4>
            <p className="text-foreground-400 text-sm">Los Angeles, CA</p>
            <p className="text-foreground-400 text-sm">United States</p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <a href="#" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Instagram</a>
            <a href="#" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">TikTok</a>
            <a href="#" className="text-foreground-400 hover:text-background-50 text-sm underline underline-offset-4 transition-colors cursor-pointer">Reddit</a>
          </div>
          <p className="text-foreground-500 text-xs text-center">
            &copy; {new Date().getFullYear()} CSSSpreadsheet.fun. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-foreground-400 hover:text-background-50 text-xs underline underline-offset-4 transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" className="text-foreground-400 hover:text-background-50 text-xs underline underline-offset-4 transition-colors cursor-pointer">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}